import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { CACHED_HEADER } from '@evorait/pace-offline';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { OdataMeasurementToolsInterface } from '../../interfaces/odata-measurement-tools.interface';
import { OdataResponse } from '../../interfaces/online-only.interface';
import {
    MeasurementToolsDetailsModel,
    ODataMeasurementToolsModel,
} from '../../models/low-level/measurement-tools-details.model';
import { MeasurementToolState } from '../../state/measurement-tools/measurement-tools-collection.state';
import { UriUtilitiesService } from '../../services/uri-utilities/uri-utilities.service';
import { UserMasterInstanceToken, UserMasterInterface } from '../../interfaces/user-master.interface';
import { ODataUtilities } from '../../utilities/odata-utilities';

@Injectable({
    providedIn: 'root',
})
export class OdataMeasurementToolsService implements OdataMeasurementToolsInterface {
    public constructor(
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
        private readonly http: HttpClient,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly uriUtilities: UriUtilitiesService,
    ) {}

    public getMeasurementTools(): Observable<MeasurementToolsDetailsModel[]> {
        return this.request$().pipe(
            map((results) => results.map((result) => MeasurementToolsDetailsModel.fromODataMeasurementTools(result))),
            catchError((err) => {
                this.log.error(
                    'OdataMeasurementToolsService: getMeasurementTools: request error:  ' + JSON.stringify(err),
                );
                return throwError(() => err);
            }),
        );
    }

    public getMeasurementToolsState(): Observable<MeasurementToolState[]> {
        return this.request$().pipe(
            map((results) =>
                results.map((result): MeasurementToolState => ({
                    technicalId: result.TechIdentNo,
                    descriptionDropdownText: result.EquipmentDesc,
                    calibratedDate: result.Mwert1,
                    gid: result.FrontId,
                    message: result.Message,
                })),
            ),
            catchError((err) => {
                this.log.error(
                    'OdataMeasurementToolsService: getMeasurementToolsState: request error:  ' + JSON.stringify(err),
                );
                return throwError(() => err);
            }),
        );
    }

    private request$(): Observable<ODataMeasurementToolsModel[]> {
        const userModel = this.userMasterService.getSelectedUserModel();
        const query = this.uriUtilities.appendUriFilters(
            this.uriUtilities.getUriForContent() + ENDPOINTS.MeasToolSet,
            userModel,
        );
        const headers = new HttpHeaders({ [CACHED_HEADER]: CACHED_HEADER });

        return this.http
            .get<OdataResponse<ODataMeasurementToolsModel>>(query, { headers })
            .pipe(map((response) => ODataUtilities.transformResponseDataToArray(response.d)));
    }
}
